import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TextField, Select, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { accountsApi } from '../api/client';
import { formatDate } from '../utils/formatTimestamp';
import styles from './Moderation.module.css';

const statusOptions = [
  { value: 'banned', label: 'Banned' },
  { value: 'suspended', label: 'Suspended' },
];

export function Moderation() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('banned');
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['moderated-accounts', { search, status, limit, offset: page * limit }],
    queryFn: () =>
      accountsApi.list({
        search: search || undefined,
        status,
        limit,
        offset: page * limit,
      }),
  });

  const totalPages = Math.ceil((data?.pagination.total || 0) / limit);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Moderation</h1>

      <div className={styles.filters}>
        <TextField
          name="search"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />

        <Select
          name="status"
          options={statusOptions}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
        />
      </div>

      <div className={styles.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.accounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>
                  <span className={styles.username}>{account.username_display}</span>
                </TableCell>
                <TableCell>{account.email}</TableCell>
                <TableCell>
                  <span className={`${styles.status} ${styles[`status${account.status}`]}`}>
                    {account.status}
                  </span>
                </TableCell>
                <TableCell>
                  {formatDate(account.created_at)}
                </TableCell>
                <TableCell>
                  <Link to={`/accounts/${account.id}`} className={styles.viewLink}>
                    View Details
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isLoading && <div className={styles.loading}>Loading...</div>}
        {!isLoading && !data?.accounts.length && (
          <div className={styles.empty}>
            No {status} accounts found
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
